import { Box, Grid, Typography, getContrastRatio } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSsoActions from "./useSsoActions";
import { resetState } from "../../../redux/commonSlice";
import ErrorHeader from "../../shared/ErrorHeader";
import { SSO_ERRORS } from "./ssoConstants";
import ErrorPageImage from "../../shared/assets/ErrorPageImage";
import CustomButton from "../../../components/CustomButton";
import Footer from "../components/Footer";
import { generateLighterContrastColor } from "../../../utils/commonServices";

function decodeJwtPayload(token) {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
}

const SsoRedirect = (props) => {
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true)
    const [banner, setBanner] = useState("Please wait while we redirect for authentication...")
    const {oktaSignin, entraSignin} = useSsoActions();
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const primary = localStorage.getItem('primary')
    const secondary = localStorage.getItem('secondary')
    const isAlreadySignedIn = !!window.localStorage.getItem('token')
  const token = searchParams.get("token");
  const authMethod = searchParams.get("auth_method");
  const error = searchParams.get("e");
  const logout = () => {
    window.localStorage.removeItem('token')
    dispatch(resetState())
    navigate('/signin')
  }

  const apiCallback = (success) => {
    setIsLoading(false);
    if(!success) {
        setBanner("Authentication failed! Please try again or contact support.")
    }
  }

  useEffect(()=> {
    if(!!token && !!authMethod) {
        const payload = {token}

        if (authMethod === 'saml') {
          try {
            const jwtPayload = decodeJwtPayload(token);
            if (jwtPayload.org_token) {
              // Entra ID SSO — JWT contains org_token
              window.localStorage.setItem('is_entra_user', true)
              window.localStorage.removeItem('is_okta_user')
              entraSignin(payload, apiCallback)
            } else {
              // Okta SSO — no org_token in JWT
              window.localStorage.setItem('is_okta_user', true)
              window.localStorage.removeItem('is_entra_user')
              oktaSignin(payload, apiCallback)
            }
          } catch (e) {
            // If JWT decode fails, fall back to Okta flow
            window.localStorage.setItem('is_okta_user', true)
            window.localStorage.removeItem('is_entra_user')
            oktaSignin(payload, apiCallback)
          }
        } else {
          window.localStorage.setItem('is_okta_user', true)
          window.localStorage.removeItem('is_entra_user')
          oktaSignin(payload, apiCallback)
        }
    } else {
      setTimeout(()=> {
        if(!!error){
          setBanner(SSO_ERRORS[error] || "Authentication failed! Please try again or contact support.")
          setIsLoading(false)
          return
        }
        setBanner("Authentication failed! Please try again or contact support.")
        setIsLoading(false)
      }, 5000)
    }
  }, [token, authMethod])
  
    return isLoading ? (
    <Box sx={{ display: 'flex', color: '#000000', backgroundColor: '#ffffff', width: '100%', height: '100vh', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Typography fontSize={34} p={8}>{banner}</Typography>
    </Box>
  ) : (
    <Box sx={{ width: '100%', position: 'absolute', height: '100%', minWidth: '100vw', backgroundImage: primary === null && secondary === null ? 'linear-gradient(110deg, #F7F7F730,  #E6E6E650, #F7F7F730 , #E6E6E650, #E6E6E670,#00CEFF20 , #00CEFF50 80%,  #00D74460 , #00D74460 )' : `linear-gradient(110deg, #F7F7F730,  #E6E6E650, #F7F7F730 , #E6E6E650, #E6E6E670,${secondary}20 , ${secondary}50 80%,  ${primary}60 , ${primary}60 )` }}>
      <ErrorHeader />
      <Box
        sx={{
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarColor: '#FFFFFF00',
          height: 'calc(100% - 130px)',
          marginTop: { xs: '80px', lg: '80px' },
          px: { xs: '1vw', sm: '3vw', md: '6vw', lg: '6vw', xl: '6vw' },
          display: 'flex',
          width: '100%',
          maxWidth: '1920px',
          margin: 'auto',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Box pl={{ sm: 10 }} pb={4}>
              <Box flexDirection="column">
                <Box sx={{ marginY: { xs: '15px', md: '20px', lg: '25px', xl: '38px' } }}>
                  <Typography
                    variant="h4"
                    style={{ fontWeight: 600, color: '#08231B' }}
                    sx={{ fontSize: { xs: '30px', sm: '40px', md: '40px', lg: '50px', xl: '80px' }, fontFamily: 'Rubik' }}
                  >
                    Oh Shoot.
                  </Typography>
                </Box>
                <Box item xs={11} sm={8} md={7} gap={0.8} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ color: '#707070', fontSize: { xs: '12px', md: '14px', lg: '16px', xl: '20px' }, fontFamily: 'Helvetica' }}>
                    {banner}
                  </Typography>
                </Box>
                {error === '3f7c9e8a-6b2d-4f1a-9c5e-b8d7a4126e3f' && isAlreadySignedIn && (
                  <Box width="100%" sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: { xs: 'flex-start', sm: 'flex-start' }, alignItems: { xs: 'center', sm: 'flex-start' } }}>
                    <CustomButton
                      variant="contained"
                      btnText="Sign out"
                      style={{ borderRadius: '5px', border: 'none', textTransform: 'none' }}
                      onClick={logout}
                      sx={{ fontSize: { xs: '10px', sm: '10px', md: '11px', lg: '12px', xl: '16px' }, fontWeight: 600, height: { xs: '18px', sm: '20px', md: '22px', lg: '24px', xl: '32px' }, width: { xs: '100px', lg: '120px', xl: '160px' }, fontFamily: 'Rubik', backgroundColor: '#08231B !important', color: primary === null ? '#00d744' : getContrastRatio('#08231B', primary) > 4.5 ? `${primary}` : generateLighterContrastColor(primary) }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6} className="flex j-center a-center">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} pl={{ xs: 2, sm: 4, md: 8, lg: 10, xl: 12 }}>
              <Box sx={{ height: { xs: 180, sm: 200, md: 220, lg: '240px', xl: 300 }, marginBottom: { xs: '15px', sm: '15px', md: '20px', lg: '22.66px', xl: '34px' } }}>
                <ErrorPageImage sx={{ height: '100%', width: '100%', maxWidth: '350px' }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Footer />
    </Box>
  )
}

export default SsoRedirect;